/**
 * Modulation: LFO routes (sine/tri as OscillatorNode with a phase-shifted PeriodicWave; random/perlin as a
 * ConstantSourceNode driven from the 30 Hz engine ticker with setTargetAtTime), a tiny 1-D Perlin/value noise,
 * and the Ticker that the engine drives from a single JS timer (never per audio quantum).
 */
import { targetTau } from './params';

export type LfoShape = 'sine' | 'tri' | 'random' | 'perlin';

/** Fourier coefficients for a sine or triangle wave shifted by `phase` (0..1 of a cycle). */
export function phasedWaveCoefficients(shape: 'sine' | 'tri', phase: number, harmonics = 15): { real: Float32Array; imag: Float32Array } {
  const real = new Float32Array(harmonics + 1);
  const imag = new Float32Array(harmonics + 1);
  const phi = 2 * Math.PI * phase;
  if (shape === 'sine') {
    real[1] = Math.sin(phi);
    imag[1] = Math.cos(phi);
    return { real, imag };
  }
  // triangle: 8/pi^2 * sum_k (-1)^k sin((2k+1)x) / (2k+1)^2
  for (let k = 0; 2 * k + 1 <= harmonics; k++) {
    const n = 2 * k + 1;
    const a = ((8 / (Math.PI * Math.PI)) * (k % 2 === 0 ? 1 : -1)) / (n * n);
    real[n] = a * Math.sin(n * phi);
    imag[n] = a * Math.cos(n * phi);
  }
  return { real, imag };
}

/** mulberry32 PRNG: deterministic for tests. */
export function makeRng(seed: number): () => number {
  let a = seed >>> 0 || 1;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** 1-D value noise with smoothstep interpolation; output roughly -1..1, continuous in x. */
export class Perlin1D {
  private readonly table: Float32Array;
  constructor(seed = 1, size = 256) {
    const rng = makeRng(seed);
    this.table = new Float32Array(size);
    for (let i = 0; i < size; i++) this.table[i] = rng() * 2 - 1;
  }
  at(x: number): number {
    const n = this.table.length;
    const xi = Math.floor(x);
    const f = x - xi;
    const s = f * f * (3 - 2 * f);
    const a = this.table[((xi % n) + n) % n]!;
    const b = this.table[(((xi + 1) % n) + n) % n]!;
    return a + (b - a) * s;
  }
}

export type TickFn = (now: number, dt: number) => void;

/** A set of callbacks the engine runs at ~30 Hz. */
export class Ticker {
  private readonly fns = new Set<TickFn>();
  add(fn: TickFn): () => void {
    this.fns.add(fn);
    return () => { this.fns.delete(fn); };
  }
  run(now: number, dt: number): void {
    for (const fn of Array.from(this.fns)) {
      try { fn(now, dt); } catch (e) { console.warn('[tonist] tick error', e); }
    }
  }
  get size(): number { return this.fns.size; }
}

export const ticker = new Ticker();

export interface LfoOptions {
  shape: LfoShape;
  rateHz: number;
  /** depth in target param units (the LFO output is -depth..depth added to the param's own value) */
  depth: number;
  /** 0..1 start phase for sine/tri, seed for random/perlin */
  phase?: number;
  seed?: number;
}

/**
 * An additive LFO into an AudioParam. The param keeps its own base value; the LFO adds ±depth.
 * sine/tri: OscillatorNode → GainNode(depth) → param. random/perlin: ConstantSourceNode → GainNode(depth) → param,
 * where the constant is re-targeted from the ticker.
 */
export class LfoRoute {
  private readonly depthGain: GainNode;
  private osc: OscillatorNode | null = null;
  private dc: ConstantSourceNode | null = null;
  private untick: (() => void) | null = null;
  private rate: number;
  constructor(private readonly ctx: BaseAudioContext, opts: LfoOptions, target: AudioParam) {
    this.rate = opts.rateHz;
    this.depthGain = ctx.createGain();
    this.depthGain.gain.value = opts.depth;
    this.depthGain.connect(target);
    const now = ctx.currentTime;
    if (opts.shape === 'sine' || opts.shape === 'tri') {
      const osc = ctx.createOscillator();
      const c = phasedWaveCoefficients(opts.shape, opts.phase ?? 0);
      osc.setPeriodicWave(ctx.createPeriodicWave(c.real, c.imag, { disableNormalization: true }));
      osc.frequency.value = Math.max(0, opts.rateHz);
      osc.connect(this.depthGain);
      osc.start(now);
      this.osc = osc;
    } else {
      const dc = ctx.createConstantSource();
      dc.offset.value = 0;
      dc.connect(this.depthGain);
      dc.start(now);
      this.dc = dc;
      const perlin = new Perlin1D(opts.seed ?? Math.floor(Math.random() * 1e9));
      let phase = (opts.phase ?? Math.random()) * 100;
      let lastCell = Math.floor(phase);
      let held = perlin.at(phase);
      const random = opts.shape === 'random';
      this.untick = ticker.add((t, dt) => {
        phase += this.rate * dt;
        if (random) {
          const cell = Math.floor(phase);
          if (cell !== lastCell) { lastCell = cell; held = perlin.at(cell + 0.5) * 1.2; }
          const period = this.rate > 0 ? 1 / this.rate : 1;
          targetTau(dc.offset, Math.max(-1, Math.min(1, held)), t, Math.min(0.3, period * 0.15));
        } else {
          targetTau(dc.offset, perlin.at(phase), t, 0.06);
        }
      });
    }
  }
  setDepth(depth: number, t: number, seconds = 0.2): void {
    targetTau(this.depthGain.gain, depth, t, seconds / 4.6);
  }
  setRate(rateHz: number, t: number): void {
    this.rate = rateHz;
    if (this.osc) targetTau(this.osc.frequency, Math.max(0, rateHz), t, 0.1);
  }
  dispose(): void {
    const t = this.ctx.currentTime + 0.05;
    try { this.osc?.stop(t); } catch { /* already stopped */ }
    try { this.dc?.stop(t); } catch { /* already stopped */ }
    this.untick?.();
    setTimeout(() => {
      try { this.osc?.disconnect(); this.dc?.disconnect(); this.depthGain.disconnect(); } catch { /* gone */ }
    }, 100);
  }
}
